import React, { ChangeEvent, KeyboardEvent, useEffect, useRef } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import {
  Avatar,
  Button,
  Col,
  Collapse,
  Input,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  message
} from 'antd'
import moment from 'moment'
import { AppstoreOutlined, ClockCircleOutlined, DeleteOutlined, DollarCircleOutlined, EditOutlined, EyeOutlined, FieldTimeOutlined, FileDoneOutlined, FileExcelOutlined, FilePptOutlined, HistoryOutlined, LinkOutlined, ReadOutlined, TagOutlined, TagsOutlined } from '@ant-design/icons'
import { globalStore } from '@/common'
import { contexts } from '@/domains'
import { DeliveryItem, UserInfoItem } from '@/domains/hooks/useDelivery' 
import DeliveryTag from './DeliveryTag'
import CommentList from './CommentList'
import { useLocalStorage } from '@/utils/useSparrowLocalStorage'
import { formatTimeDifference } from '@/utils/S'
import AutoLink from '@/components/AutoLink/index';

const { TextArea } = Input

export type Store = {
  currTab: 'comment' | 'operationRecord'
  comment: string
  isFocus: boolean
}

const { DeliveryContext } = contexts

function DeliveryCardItem(
  props: DeliveryItem & {
    onClick: (deliveryItem: DeliveryItem) => void
    onDelete: (deliveryItem: DeliveryItem) => void
  }
) {
  const {
    id,
    taskName,
    taskStatusId,
    userList,
    projectId,
    projectName,
    morningMeetingId,
    businessLineId,
    taskTypeId,
    remarks,
    finishDate,
    taskUrl,
    onClick,
    onDelete
  } = props
  const [token, setToken] = useLocalStorage<string>({
    key: 'token'
  })
  const {
    commentListMap,
    currOperationInfos,
    handleQueryCommentListByTargetIds,
    handleCreateComment,
    refreshCommentList,
    handleQueryOperationListById
  } = DeliveryContext.useContainer()
  const store = useLocalObservable<Store>(() => ({
    currTab: 'comment',
    comment: '',
    isFocus: false
  }))
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const isComposingRef = useRef(false)

  const handleCommentChange = (e) => {
    store.comment = e.target.value
  }

  const handleFocus = () => {
    store.isFocus = true
  }

  const handleBlur = () => {
    store.isFocus = false
  }

  const insertCommentNewline = (el: HTMLTextAreaElement) => {
    const { selectionStart, selectionEnd, value } = el
    const next = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`
    store.comment = next
    const cursor = selectionStart + 1
    requestAnimationFrame(() => {
      el.selectionStart = cursor
      el.selectionEnd = cursor
    })
  }

  const handleCommentPressEnter = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isComposingRef.current || e.nativeEvent.isComposing) {
      return
    }
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      insertCommentNewline(e.currentTarget)
      return
    }
    if (e.shiftKey) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    submitComment()
  }

  const submitComment = () => {
    if (!store.comment?.trim()) {
      message.error('评论不能为空')
      return
    }
    const payload = {
      content: store.comment.trim(),
      targetId: String(id),
      targetType: 'SPARROW',
      toUserId: '0'
    }

    handleCreateComment(payload).then((res) => {
      if (res) {
        store.comment = ''
        refreshCommentList()
      }
    })
  }

  useEffect(() => {
    handleQueryOperationListById(id)
  }, [id])


  return (
    <div className="delivery-card-item"> 
      <Space className="w-full" size={15} direction="vertical"> 
          <Row>
              <Col span={12} className='deliver_right_content icon-info-list-container'>
                  <Row className='first-row'>
                        <Col span={1}></Col>
                        <Col span={2}>
                            <ClockCircleOutlined />
                        </Col>
                        <Col span={21}>
                            <span>{moment(finishDate).format('YYYY年MM月DD日')}  前完成</span>
                        </Col>
                  </Row>
                  <Row>
                        <Col span={1}></Col>
                        <Col span={2}>
                            <TagOutlined />
                        </Col>
                        <Col span={21} >
                            <span className="two-parts-tag">
                                <span className="type">交付类型</span>
                                <span className="value">{globalStore.taskTypeRecord[taskTypeId] && globalStore.taskTypeRecord[taskTypeId].name}</span>
                            </span>
                            {!!projectName?.trim() && (
                              <span className="two-parts-tag">
                                  <span className="type">所属项目</span>
                                  <span className="value">{projectName}</span>
                              </span>
                            )}
                        </Col>
                  </Row>
                  <Row>
                        <Col span={1}></Col>
                        <Col span={2}> 
                            <FilePptOutlined />
                        </Col>
                        <Col span={21}>
                        {
                          taskUrl?(
                            <AutoLink url={taskUrl} />
                          ):'暂无文档链接'
                        }
                        </Col>
                  </Row>
                  <Row>
                          <Col span={1}></Col>
                          <Col span={2}>
                              <ReadOutlined  />
                          </Col>
                          <Col span={19}>
                                <span>
                                    {
                                      remarks?(
                                            remarks.split('\n').map(
                                              (text: string, index: number): JSX.Element => (
                                                <p
                                                  key={index}
                                                  style={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-all'
                                                  }}
                                                >
                                                  {text}
                                                </p>
                                              )
                                            )
                                      ):(
                                          '暂无备注'
                                      )
                                    }
                                </span>
                          </Col>
                          <Col span={2}></Col>
                  </Row>
                  <Row className='last-row'>
                          <Col span={1}></Col>
                          <Col span={2}>  
                              <EyeOutlined style={{color:'#b9b8b8',fontSize:'16px'}}/>
                          </Col>
                          <Col span={21} className='event-list'>
                              {
                                // 操作日志
                                currOperationInfos.map( (operation)=>(
                                      <div key ={operation.id}> 
                                        <span className='name-hint'>{operation.userName} </span>
                                        <span>{operation.content}</span>
                                        <span className='time-desc'>{operation.eventTime?formatTimeDifference(operation.eventTime):''} </span>
                                      </div> 
                                  )
                                )
                              } 
                          </Col>
                  </Row>
              </Col>
              <Col span={4} className='deliver_middle_content' >
                        <Space size={0} direction="horizontal" className='w-full' align="center">
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onClick(props)
                                }}
                              >
                                编辑
                              </Button>
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onDelete(props)
                                }}
                              >
                                删除
                            </Button>
                        </Space> 
              </Col>
              <Col span={8}  className='deliver_left_content'>
                  <div className="flex flex-col" style={{ padding: '0 20px' }}>
                    <TextArea
                      allowClear
                      ref={inputRef}
                      autoSize={{ minRows: 1, maxRows: 2 }}
                      placeholder="Enter 发送，Ctrl/⌘ + Enter 换行"
                      value={store.comment}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={handleCommentChange}
                      onCompositionStart={() => {
                        isComposingRef.current = true
                      }}
                      onCompositionEnd={() => {
                        isComposingRef.current = false
                      }}
                      onPressEnter={handleCommentPressEnter}
                      className="comment-content-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div style={{textAlign:'left',fontSize:13, paddingBlock:12}}>评论({commentListMap[id]? commentListMap[id].length : 0})</div>
                    <CommentList newDataSource={null} oldDataSource={commentListMap[id] || []} />
                  </div>
              </Col>
          </Row> 
      </Space>
    </div>
  )
}

export default observer(DeliveryCardItem)
